import React from 'react';
import { Box, Typography, Paper, Tabs, Tab, useTheme } from '@mui/material';
import { Code as CodeIcon } from '@mui/icons-material';
import CodeBlock from './CodeBlock';

const UsageExample = ({ 
  title, 
  description, 
  examples, 
  defaultTab = 0,
  sx = {} 
}) => {
  const [activeTab, setActiveTab] = React.useState(defaultTab);
  const theme = useTheme();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Paper
      elevation={theme.palette.mode === 'dark' ? 3 : 2}
      sx={{
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'divider',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'background.paper',
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0,0,0,0.4)' 
          : '0 4px 20px rgba(0,0,0,0.05)',
        ...sx
      }}
    >
      <Box sx={{ 
        p: 2, 
        bgcolor: theme.palette.mode === 'dark' 
          ? 'rgba(255,255,255,0.05)' 
          : 'grey.50' 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: description ? 1 : 0 }}>
          <CodeIcon sx={{ 
            mr: 1.5, 
            color: theme.palette.mode === 'dark' 
              ? theme.palette.primary.light 
              : theme.palette.primary.main,
            filter: theme.palette.mode === 'dark' ? 'drop-shadow(0 0 2px rgba(33, 150, 243, 0.5))' : 'none'
          }} />
          <Typography 
            variant="h6" 
            component="div" 
            fontWeight="bold"
            sx={{
              color: theme.palette.mode === 'dark' 
                ? theme.palette.primary.light 
                : theme.palette.primary.main
            }}
          >
            {title}
          </Typography>
        </Box>
        {description && (
          <Typography 
            variant="body2" 
            sx={{ 
              ml: 4, 
              color: theme.palette.text.secondary 
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      
      {examples.length > 1 && (
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'divider',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'background.paper',
            '& .MuiTab-root': {
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : undefined,
              '&.Mui-selected': {
                color: theme.palette.mode === 'dark' ? theme.palette.primary.light : undefined,
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : undefined,
            }
          }}
        >
          {examples.map((example, index) => (
            <Tab key={index} label={example.label} />
          ))}
        </Tabs>
      )}
      
      <Box sx={{ p: 0 }}>
        {examples.map((example, index) => (
          <Box 
            key={index} 
            sx={{ 
              display: activeTab === index ? 'block' : 'none',
              '& > *': { borderRadius: 0 }
            }}
          >
            <CodeBlock 
              language={example.language} 
              code={example.code}
              showLineNumbers={example.showLineNumbers !== false}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default UsageExample;